// Schreiben-Assistent (#586): Empfänger mit Anschrift, E-Mail, Häkchen „Postzustellung“ und Platzhalter-Werten –
// einzeln (Suche) oder alle eines Objekts (gleiche Auswahl und Aktiv-Regeln wie der Serienmail-Assistent).
import { supabase } from '../supabase/client';
import { ladeEmpfaenger, type EmpfaengerTreffer } from '../dokumente/briefApi';
import { kandidatAnschrift } from '../epost/serienbrief';
import { ladeKandidaten } from '../serienmail/api';
import type { Kandidat, Zielgruppe } from '../serienmail/typen';
import { KONTAKT_ZUSTELLWEGE, MAX_EMPFAENGER, type EmpfaengerArt, type SchreibenEmpfaenger, type Zustellweg } from './typen';

const BASIS: Record<EmpfaengerArt, string> = { mieter: 'email, einheit_id, profile_id', eigentuemer: 'email, profile_id', kontakt: 'email', dienstleister: 'email' };
const TABELLE: Record<EmpfaengerArt, string> = { mieter: 'mieter', eigentuemer: 'eigentuemer', kontakt: 'kontakte', dienstleister: 'dienstleister' };

/** Reine Abbildung eines Serienmail-Kandidaten (Fallback, wenn die Einzeldaten nicht ladbar sind). */
export function ausKandidat(k: Kandidat, werte: Record<string, string>): SchreibenEmpfaenger {
  return {
    art: k.owner_type, id: k.owner_id, name: k.name, anschrift: kandidatAnschrift(k).text, email: k.email,
    zustellwege: [], portal: false, objektId: k.objekt_id, einheitId: k.einheit_id, ordnerName: null, werte,
  };
}

const spalteFehlt = (e: { code?: string } | null) => !!e && ['42703', 'PGRST204'].includes(e.code ?? '');

type Zeile = { email?: string | null; zustellwege?: string[] | null; sperrvermerk?: string | null; einheit_id?: string | null; profile_id?: string | null };

/** Haupt-E-Mail (kontakt_kanaele, primär zuerst), bevorzugte Zustellwege (#587), Sperrvermerk (Etappe 3), Portalzugang und Einheit. */
async function zusatz(art: EmpfaengerArt, id: string): Promise<{ email: string | null; zustellwege: Zustellweg[]; sperrvermerk: string | null; portal: boolean; einheitId: string | null }> {
  // sofort anstoßen (Abfragen starten erst mit then) – läuft parallel zur Stammdaten-Abfrage
  const kanalAnfrage = Promise.resolve(supabase.from('kontakt_kanaele').select('wert').eq('owner_id', id).eq('kanal', 'email')
    .order('ist_primaer', { ascending: false }).order('sortierung').limit(1).maybeSingle());
  // zustellwege/sperrvermerk kommen mit den Migrationen – fehlen sie noch, gilt „automatisch“ bzw. kein Vermerk
  let z: Zeile | null = null;
  for (const extra of [', zustellwege, sperrvermerk', ', zustellwege', '']) {
    const r = await supabase.from(TABELLE[art]).select(`${BASIS[art]}${extra}`).eq('id', id).maybeSingle();
    if (!r.error) { z = r.data as Zeile | null; break; }
    // nur „Spalte fehlt“ führt zur kleineren Abfrage – jeder andere Fehler, damit kein Sperrvermerk still verloren geht
    if (!spalteFehlt(r.error)) throw new Error(`Empfänger nicht geladen: ${r.error.message}`);
  }
  const kanal = await kanalAnfrage;
  return {
    email: ((kanal.data?.wert as string | undefined) ?? z?.email ?? '').trim() || null,
    zustellwege: (z?.zustellwege ?? []).filter((w): w is Zustellweg => (KONTAKT_ZUSTELLWEGE as string[]).includes(w)),
    sperrvermerk: z?.sperrvermerk?.trim() || null,
    portal: (art === 'mieter' || art === 'eigentuemer') && !!z?.profile_id,
    einheitId: z?.einheit_id ?? null,
  };
}

export async function ladeSchreibenEmpfaenger(t: EmpfaengerTreffer): Promise<SchreibenEmpfaenger> {
  const [d, z] = await Promise.all([ladeEmpfaenger(t), zusatz(t.art, t.id)]);
  return { art: d.art, id: d.id, name: d.name, anschrift: d.anschrift, objektId: d.objektId, ordnerName: d.ordnerName, werte: d.werte, ...z };
}

/** Neue Kandidaten (ohne Doppelte und schon Gewählte) – alle oder keiner: passt die Menge nicht in den Platz,
 *  wird niemand übernommen (sonst fehlten bei „alle Objekte“ still die übrigen). Reine Funktion. */
export function auswahlFuerPlatz(kandidaten: Kandidat[], vorhanden: Set<string>, platz: number): { neu: Kandidat[]; gesamt: number; passt: boolean } {
  const gesehen = new Set(vorhanden);
  const neu = kandidaten.filter((k) => { const s = `${k.owner_type}:${k.owner_id}`; if (gesehen.has(s)) return false; gesehen.add(s); return true; });
  return neu.length > platz ? { neu: [], gesamt: neu.length, passt: false } : { neu, gesamt: neu.length, passt: true };
}

/** Alle aktiven Empfänger der gewählten Gruppen in den Objekten – nur, wenn alle in den freien Platz passen. */
export async function ladeObjektEmpfaenger(objektIds: string[], gruppen: Zielgruppe[], vorhanden: Set<string> = new Set(), platz = MAX_EMPFAENGER): Promise<{ empfaenger: SchreibenEmpfaenger[]; gesamt: number; passt: boolean }> {
  const { kandidaten } = await ladeKandidaten(gruppen, { objektIds, einheitIds: [] });
  const { neu, gesamt, passt } = auswahlFuerPlatz(kandidaten, vorhanden, platz);
  const aus: SchreibenEmpfaenger[] = [];
  for (let i = 0; i < neu.length; i += 6) {
    aus.push(...await Promise.all(neu.slice(i, i + 6).map((k) =>
      ladeSchreibenEmpfaenger({ art: k.owner_type, id: k.owner_id, name: k.name, sub: '' }).catch(() => ausKandidat(k, {})))));
  }
  return { empfaenger: aus, gesamt, passt };
}

/** Etappe 3: frische Sperrvermerke (Schlüssel `${art}:${id}`) in die Empfänger übernehmen – reine Funktion. */
export function mitSperrvermerken(liste: SchreibenEmpfaenger[], vermerke: Map<string, string>): SchreibenEmpfaenger[] {
  return liste.map((e) => ({ ...e, sperrvermerk: vermerke.get(`${e.art}:${e.id}`) ?? null }));
}

/** Sperrvermerke aller Empfänger unmittelbar vor dem Senden frisch laden (eine Abfrage je Tabelle).
 *  Fehlt die Spalte (Migration), gibt es keine Vermerke; jeder andere Fehler bricht das Senden ab. */
export async function ladeSperrvermerke(liste: Pick<SchreibenEmpfaenger, 'art' | 'id'>[]): Promise<Map<string, string>> {
  const aus = new Map<string, string>();
  for (const art of Object.keys(TABELLE) as EmpfaengerArt[]) {
    const ids = [...new Set(liste.filter((e) => e.art === art).map((e) => e.id))];
    for (let i = 0; i < ids.length; i += 100) {
      const { data, error } = await supabase.from(TABELLE[art]).select('id, sperrvermerk').in('id', ids.slice(i, i + 100));
      if (spalteFehlt(error)) return aus;
      if (error) throw new Error(`Sperrvermerke nicht prüfbar – nichts gesendet: ${error.message}`);
      for (const z of (data ?? []) as { id: string; sperrvermerk: string | null }[]) if (z.sperrvermerk?.trim()) aus.set(`${art}:${z.id}`, z.sperrvermerk.trim());
    }
  }
  return aus;
}
